import { useState } from "react";
import axios from "axios";
import { Globe, Search, Server, CheckCircle2, XCircle, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { API } from "../../App";

const FLAG_EMOJI = {
  ES: "\u{1F1EA}\u{1F1F8}", FR: "\u{1F1EB}\u{1F1F7}", IT: "\u{1F1EE}\u{1F1F9}",
  DE: "\u{1F1E9}\u{1F1EA}", CA: "\u{1F1E8}\u{1F1E6}", US: "\u{1F1FA}\u{1F1F8}",
  MA: "\u{1F1F2}\u{1F1E6}",
};

export default function DnsHostChecker({ token }) {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleCheck = async () => {
    if (!domain.trim()) return toast.error("Entrez un domaine");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/user/tools/dns-check`, { domain }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
      setResult(data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Erreur de vérification DNS";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const pctColor = (pct) => {
    if (pct >= 80) return "text-emerald-400";
    if (pct >= 50) return "text-amber-400";
    return "text-red-400";
  };

  const barColor = (pct) => {
    if (pct >= 80) return "bg-emerald-500";
    if (pct >= 50) return "bg-amber-500";
    return "bg-red-500";
  };

  return (
    <div className="space-y-5" data-testid="dns-host-checker">
      {/* Input */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="dns-domain-input"
            placeholder="Entrez un domaine (ex: panelhelper.xyz)"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCheck()}
            className="pl-10 bg-background border-border"
          />
        </div>
        <Button
          data-testid="dns-check-btn"
          onClick={handleCheck}
          disabled={loading}
          className="gap-2 min-w-[140px]"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          {loading ? "Analyse..." : "Check DNS"}
        </Button>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4 animate-in fade-in duration-300">
          {/* Global Progress */}
          <div className="rounded-lg border border-border bg-card p-4" data-testid="dns-global-result">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Server className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm">{result.domain}</span>
              </div>
              <span className={`text-2xl font-bold font-mono ${pctColor(result.success_pct)}`}>
                {result.success_pct}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2.5 mb-2">
              <div
                className={`h-2.5 rounded-full transition-all duration-700 ${barColor(result.success_pct)}`}
                style={{ width: `${result.success_pct}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {result.total_success}/{result.total_isps} serveurs DNS ont resolu le domaine
            </p>
          </div>

          {/* Country Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="dns-country-cards">
            {result.countries.map((c) => {
              const pct = c.total > 0 ? Math.round((c.success / c.total) * 100) : 0;
              return (
                <div
                  key={c.country}
                  className={`rounded-lg border p-3 transition-colors ${
                    pct === 100 ? "border-emerald-500/30 bg-emerald-500/5" :
                    pct > 0 ? "border-amber-500/30 bg-amber-500/5" :
                    "border-red-500/30 bg-red-500/5"
                  }`}
                  data-testid={`dns-country-${c.flag}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{FLAG_EMOJI[c.flag] || c.flag}</span>
                    <span className="text-sm font-medium truncate">{c.country}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-lg font-bold font-mono ${pctColor(pct)}`}>
                      {c.success}/{c.total}
                    </span>
                    {pct === 100 ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    ) : pct > 0 ? (
                      <CheckCircle2 className="h-4 w-4 text-amber-400" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-400" />
                    )}
                  </div>
                  <div className="w-full bg-muted rounded-full h-1 mt-1.5">
                    <div
                      className={`h-1 rounded-full ${barColor(pct)}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Details Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDetails(!showDetails)}
            className="w-full gap-2 text-muted-foreground"
            data-testid="dns-toggle-details"
          >
            {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {showDetails ? "Masquer les details" : "Voir les details par ISP"}
          </Button>

          {/* ISP Table */}
          {showDetails && (
            <div className="rounded-lg border border-border overflow-hidden" data-testid="dns-isp-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b border-border">
                    <th className="text-left p-2.5 font-medium text-muted-foreground">ISP</th>
                    <th className="text-left p-2.5 font-medium text-muted-foreground">Country</th>
                    <th className="text-center p-2.5 font-medium text-muted-foreground">Status</th>
                    <th className="text-right p-2.5 font-medium text-muted-foreground">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {result.details.map((r, i) => (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/10">
                      <td className="p-2.5 font-mono text-xs">{r.isp}</td>
                      <td className="p-2.5">
                        <span className="text-xs">{FLAG_EMOJI[r.flag] || ""} {r.country}</span>
                      </td>
                      <td className="p-2.5 text-center">
                        {r.success ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-400 mx-auto" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-400 mx-auto" />
                        )}
                      </td>
                      <td className="p-2.5 text-right font-mono text-xs text-muted-foreground">
                        {r.time_ms >= 0 ? `${r.time_ms}ms` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
